import {useCallback} from 'react'
import {AuthHelpers, useAuthHelper, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useQueryClient} from '@tanstack/react-query'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {getMrtApiBasepath} from '../ssr/helpers'
import {
    SA_EMAIL_KEY,
    SA_IMPERSONATING_KEY,
    SA_TOKEN_KEY,
    SA_SESSION_TOKEN_KEY,
} from './use-sales-assistant-session'

/**
 * Ends a "login on behalf" impersonation: logs the customer out (revoking the
 * customer token and minting a fresh guest token) while keeping the agent's
 * Gigya session intact, then re-establishes the agent guest session on the new
 * token and returns to the dashboard.
 *
 * If the server can't re-establish the agent session it clears the
 * Sales Assistant markers and sends the agent to /sales-assistant/login to re-authenticate, rather
 * than nuking the whole session — so the UI never gets stuck inconsistent.
 *
 * Returns an async `endImpersonation({onStart, onSettled, redirectTo})` function;
 * the callbacks let the caller drive its own loading indicator and `redirectTo`
 * overrides the landing route (defaults to the dashboard; guest checkout passes
 * the storefront home so the agent starts browsing as guest right away).
 */
export const useEndImpersonation = () => {
    const logout = useAuthHelper(AuthHelpers.Logout)
    const {getTokenWhenReady} = useAccessToken()
    const queryClient = useQueryClient()
    const navigate = useNavigation()

    return useCallback(
        async ({onStart, onSettled, redirectTo = '/sales-assistant/dashboard'} = {}) => {
            onStart?.()

            let salesAssistantSessionToken = null
            let salesAssistantToken = null
            try {
                salesAssistantSessionToken = localStorage.getItem(SA_SESSION_TOKEN_KEY)
                salesAssistantToken = localStorage.getItem(SA_TOKEN_KEY)
            } catch {
                // localStorage may be unavailable in some environments
            }

            try {
                localStorage.removeItem(SA_IMPERSONATING_KEY)
                localStorage.removeItem(SA_TOKEN_KEY)
                window.dispatchEvent(new CustomEvent('sa-impersonation-changed'))
            } catch {
                // localStorage may be unavailable in some environments
            }

            navigate(redirectTo)

            // Capture the customer's token before revoking it so the server can
            // clean up any residual InMemoryStorage markers tied to it.
            const previousToken = await getTokenWhenReady()
            // Log the customer out without touching the agent's session.
            await logout.mutateAsync()
            const newToken = await getTokenWhenReady()

            try {
                const response = await fetch(
                    origin + getMrtApiBasepath('/sales-assistant-end-impersonation'),
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${newToken}`,
                        },
                        body: JSON.stringify({
                            salesAssistantToken,
                            previousToken,
                            salesAssistantSessionToken,
                        }),
                    },
                )
                if (!response.ok) {
                    const bodyText = await response.text().catch(() => '')
                    throw new Error(`end-impersonation failed: ${response.status} ${bodyText}`)
                }
            } catch {
                // The customer is already logged out,
                // so the session is a plain guest now. Rather than nuking the
                // agent's session and all storage, clear only the Sales Assistant markers
                // and send the agent to re-authenticate on /sales-assistant/login.
                try {
                    localStorage.removeItem(SA_EMAIL_KEY)
                    localStorage.removeItem(SA_IMPERSONATING_KEY)
                    localStorage.removeItem(SA_TOKEN_KEY)
                    localStorage.removeItem(SA_SESSION_TOKEN_KEY)
                } catch {
                    // localStorage may be unavailable in some environments
                }
                await queryClient.invalidateQueries()
                onSettled?.()
                navigate('/sales-assistant/login')
                return
            }

            await queryClient.invalidateQueries()

            try {
                window.dispatchEvent(new CustomEvent('sa-session-refreshed'))
            } catch {
                // window may be unavailable in some environments
            }
            onSettled?.()
        },
        [logout, getTokenWhenReady, queryClient, navigate],
    )
}
